import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Ticket, CheckCircle, AlertTriangle, Plus, Pencil, Trash2, Tags } from 'lucide-react';
import { useAdminTickets, useSessionPersistedSearchParams } from '../hooks';
import { useArcheDisplayMap } from '../hooks/useArcheDisplayMap';
import {
  TicketFilters,
  AdminTicketTable,
  BulkActionsBar,
  TagBadge,
  SendTicketToChatDialog,
} from '../components';
import { ArcheFilterSelect } from '../components/ArcheFilterSelect';
import { ResolutionTimePanel } from '../components/ResolutionTimePanel';
import { ARCHE_PATH } from '../constants';
import type { TicketFiltersState } from '../components';
import api from '@/lib/api';
import type { SupportTagResponse } from '@/lib/api';

// Well-known deleted tag ID matching migration seed
const DELETED_TAG_ID = 'stag_deleted_system_0001';

type SortColumn = 'ai_priority' | 'priority' | 'status' | 'category' | 'created_at';
type SortDirection = 'asc' | 'desc';
interface SortState {
  column: SortColumn | null;
  direction: SortDirection;
}

function setOrDelete(p: URLSearchParams, key: string, value: string | undefined | null): void {
  if (value) p.set(key, value);
  else p.delete(key);
}

// The dashboard keeps its filter/sort/tag view-state in the URL, but the
// ticket detail page's "Back to Admin Dashboard" link navigates to a bare
// `/admin` (no query string), which would drop every filter on return. We
// back the URL with a per-session store so re-entering the dashboard by ANY
// path — that back link, the nav menu, or a reload — rehydrates the last view.
// The URL remains the source of truth; this is only the restore backstop.
const VIEW_STATE_STORAGE_KEY = 'support-admin-dashboard-view';
const VIEW_PARAM_KEYS = [
  'status',
  'priority',
  'categoryId',
  'sortBy',
  'search',
  'tagId',
  'arche',
  'sortCol',
  'sortDir',
] as const;

export function AdminDashboardPage() {
  // Filter / sort / tag state lives in the URL so it survives navigating into a
  // ticket and back, plus a full reload — working through "open + in progress"
  // no longer resets every time. Ticket selection stays ephemeral (local).
  const [searchParams, updateParams] = useSessionPersistedSearchParams(
    VIEW_STATE_STORAGE_KEY,
    VIEW_PARAM_KEYS,
  );
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<string>>(new Set());
  // The ticket whose "send to chat" dialog is open, or null when it's closed.
  const [sharingTicket, setSharingTicket] = useState<{
    ticketId: string;
    ticketNumber: number;
    subject: string;
  } | null>(null);

  const statuses = useMemo(() => searchParams.getAll('status'), [searchParams]);
  const filters: TicketFiltersState = useMemo(
    () => ({
      statuses: statuses as TicketFiltersState['statuses'],
      priority: (searchParams.get('priority') as TicketFiltersState['priority']) || 'all',
      categoryId: searchParams.get('categoryId') || 'all',
      sortBy: (searchParams.get('sortBy') as TicketFiltersState['sortBy']) || undefined,
      search: searchParams.get('search') || undefined,
    }),
    [searchParams, statuses],
  );
  const activeTagId = searchParams.get('tagId');
  const archeFilter = searchParams.get('arche') ?? '';
  const columnSort: SortState = useMemo(
    () => ({
      column: (searchParams.get('sortCol') as SortColumn | null) || null,
      direction: (searchParams.get('sortDir') as SortDirection) || 'desc',
    }),
    [searchParams],
  );

  // Tag management dialog state
  const [showTagManager, setShowTagManager] = useState(false);
  const [editingTag, setEditingTag] = useState<SupportTagResponse | null>(null);
  const [tagForm, setTagForm] = useState({ name: '', color: '#6B7280', description: '' });
  const [tagSaving, setTagSaving] = useState(false);

  const isDeletedTagActive = activeTagId === DELETED_TAG_ID;

  // Determine sortBy/sortOrder from either the filter sort or column sort
  const effectiveSortBy = columnSort.column || filters.sortBy || undefined;
  const effectiveSortOrder = columnSort.column
    ? columnSort.direction
    : filters.sortBy === 'ai_priority'
      ? 'desc'
      : undefined;

  const { tickets, analytics, categories, tags, loading, refresh, refreshAll } = useAdminTickets({
    status: filters.statuses.length ? filters.statuses : undefined,
    priority: filters.priority === 'all' ? undefined : filters.priority,
    categoryId: filters.categoryId === 'all' ? undefined : filters.categoryId,
    sortBy: effectiveSortBy,
    sortOrder: effectiveSortOrder,
    search: filters.search || undefined,
    tagId: activeTagId || undefined,
    archeId: archeFilter || undefined,
    includeDeleted: isDeletedTagActive || undefined,
  });

  const { metaById: archeMeta } = useArcheDisplayMap(tickets.map((t) => t.archeId));

  const navigate = useNavigate();

  const handleInlineStatusChange = useCallback(
    async (ticketId: string, status: string) => {
      await api.updateSupportTicketAdmin(ticketId, { status });
      refresh();
    },
    [refresh],
  );

  const handleBulkStatusChange = useCallback(
    async (status: string) => {
      await api.bulkUpdateSupportTickets({
        ticketIds: Array.from(selectedTicketIds),
        status,
      });
      setSelectedTicketIds(new Set());
      refresh();
    },
    [selectedTicketIds, refresh],
  );

  const handleBulkPriorityChange = useCallback(
    async (priority: string) => {
      await api.bulkUpdateSupportTickets({
        ticketIds: Array.from(selectedTicketIds),
        priority,
      });
      setSelectedTicketIds(new Set());
      refresh();
    },
    [selectedTicketIds, refresh],
  );

  const handleBulkClose = useCallback(async () => {
    await api.bulkUpdateSupportTickets({
      ticketIds: Array.from(selectedTicketIds),
      status: 'closed',
    });
    setSelectedTicketIds(new Set());
    refresh();
  }, [selectedTicketIds, refresh]);

  const handleFilterChange = (newFilters: TicketFiltersState) => {
    updateParams((p) => {
      p.delete('status');
      newFilters.statuses.forEach((s) => p.append('status', s));
      setOrDelete(p, 'priority', newFilters.priority !== 'all' ? newFilters.priority : undefined);
      setOrDelete(
        p,
        'categoryId',
        newFilters.categoryId !== 'all' ? newFilters.categoryId : undefined,
      );
      setOrDelete(p, 'sortBy', newFilters.sortBy);
      setOrDelete(p, 'search', newFilters.search);
    });
    setSelectedTicketIds(new Set());
  };

  const handleTagFilterClick = (tagId: string) => {
    updateParams((p) => {
      if (p.get('tagId') === tagId) p.delete('tagId');
      else p.set('tagId', tagId);
    });
    setSelectedTicketIds(new Set());
  };

  const handleSortChange = (newSort: SortState) => {
    updateParams((p) => {
      if (newSort.column) {
        p.set('sortCol', newSort.column);
        p.set('sortDir', newSort.direction);
        // Column sort overrides the filter-based sort.
        p.delete('sortBy');
      } else {
        p.delete('sortCol');
        p.delete('sortDir');
      }
    });
  };

  // Tag management handlers
  const openCreateTag = () => {
    setEditingTag(null);
    setTagForm({ name: '', color: '#6B7280', description: '' });
    setShowTagManager(true);
  };

  const openEditTag = (tag: SupportTagResponse) => {
    setEditingTag(tag);
    setTagForm({ name: tag.name, color: tag.color, description: tag.description || '' });
    setShowTagManager(true);
  };

  const handleSaveTag = async () => {
    setTagSaving(true);
    try {
      if (editingTag) {
        await api.updateSupportTag(editingTag.tagId, {
          name: tagForm.name,
          color: tagForm.color,
          description: tagForm.description || null,
        });
      } else {
        await api.createSupportTag({
          name: tagForm.name,
          color: tagForm.color,
          description: tagForm.description || undefined,
        });
      }
      setShowTagManager(false);
      refreshAll();
    } finally {
      setTagSaving(false);
    }
  };

  const handleDeleteTag = async (tagId: string) => {
    await api.deleteSupportTag(tagId);
    if (activeTagId === tagId) updateParams((p) => p.delete('tagId'));
    refreshAll();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Tickets</h2>
          <p className="text-sm text-muted-foreground">
            Manage tickets and monitor support performance.
          </p>
        </div>
        {tickets.length > 0 && (
          <div className="text-sm text-muted-foreground">
            {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Analytics Cards */}
      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-28 rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-28 rounded-lg" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Tickets</CardTitle>
                <Ticket className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics?.totalTickets ?? 0}</div>
                <p className="text-xs text-muted-foreground mt-1">All time</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Open Tickets</CardTitle>
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics?.openTickets ?? 0}</div>
                <p className="text-xs text-muted-foreground mt-1">Needs attention</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Resolved Tickets</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics?.resolvedTickets ?? 0}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics?.totalTickets
                    ? `${Math.round(((analytics.resolvedTickets ?? 0) / analytics.totalTickets) * 100)}% resolution rate`
                    : 'All time'}
                </p>
              </CardContent>
            </Card>
          </div>

          <ResolutionTimePanel
            avgWeek={analytics?.avgResolutionTimeHoursWeek ?? null}
            avgMonth={analytics?.avgResolutionTimeHoursMonth ?? null}
            avgAllTime={analytics?.avgResolutionTimeHours ?? null}
            weekly={analytics?.resolutionTrendWeekly ?? []}
            monthly={analytics?.resolutionTrendMonthly ?? []}
          />
        </div>
      )}

      {/* Filters */}
      <TicketFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        categories={categories}
      />
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Arche:</span>
        <ArcheFilterSelect
          value={archeFilter}
          onChange={(v) => {
            updateParams((p) => setOrDelete(p, 'arche', v || undefined));
            setSelectedTicketIds(new Set());
          }}
        />
      </div>

      {/* Tag Filter Bar */}
      {tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Tags className="h-4 w-4 text-muted-foreground" />
          {tags.map((tag) => {
            const isActive = activeTagId === tag.tagId;
            const isDeletedTag = tag.tagId === DELETED_TAG_ID;
            return (
              <TagBadge
                key={tag.tagId}
                name={tag.name}
                color={tag.color}
                active={activeTagId === null || isActive}
                onClick={() => handleTagFilterClick(tag.tagId)}
                className={isDeletedTag && !isActive ? 'ring-1 ring-red-300 ring-offset-1' : ''}
              />
            );
          })}
          {activeTagId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateParams((p) => p.delete('tagId'))}
            >
              Clear tag filter
            </Button>
          )}
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={openCreateTag}>
              <Plus className="h-3 w-3 mr-1" />
              Manage Tags
            </Button>
          </div>
        </div>
      )}
      {tags.length === 0 && !loading && (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={openCreateTag}>
            <Plus className="h-3 w-3 mr-1" />
            Create Tag
          </Button>
        </div>
      )}

      {/* Tickets Table */}
      {loading ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No tickets match the current filters.</p>
          </CardContent>
        </Card>
      ) : (
        <AdminTicketTable
          tickets={tickets}
          onTicketClick={(ticketId) => navigate(`${ARCHE_PATH}/admin/tickets/${ticketId}`)}
          selectedTicketIds={selectedTicketIds}
          onSelectionChange={setSelectedTicketIds}
          onInlineStatusChange={handleInlineStatusChange}
          sort={columnSort}
          onSortChange={handleSortChange}
          archeMetaById={archeMeta}
          onSendToChat={(ticket) =>
            setSharingTicket({
              ticketId: ticket.ticketId,
              ticketNumber: ticket.ticketNumber,
              subject: ticket.subject,
            })
          }
        />
      )}

      <SendTicketToChatDialog
        ticket={sharingTicket}
        open={sharingTicket !== null}
        onOpenChange={(open) => {
          if (!open) setSharingTicket(null);
        }}
      />

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedTicketIds.size}
        onClearSelection={() => setSelectedTicketIds(new Set())}
        onBulkStatusChange={handleBulkStatusChange}
        onBulkPriorityChange={handleBulkPriorityChange}
        onBulkClose={handleBulkClose}
      />

      {/* Tag Management Dialog */}
      <Dialog open={showTagManager} onOpenChange={setShowTagManager}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTag ? 'Edit Tag' : 'Manage Tags'}</DialogTitle>
            <DialogDescription>
              {editingTag ? 'Update the tag details.' : 'Create a new tag or manage existing ones.'}
            </DialogDescription>
          </DialogHeader>

          {/* Existing tags list (only show when not editing) */}
          {!editingTag && tags.length > 0 && (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {tags.map((tag) => (
                <div
                  key={tag.tagId}
                  className="flex items-center justify-between px-3 py-2 rounded-md border"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full border"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="text-sm font-medium">{tag.name}</span>
                    {tag.isSystem && (
                      <span className="text-xs text-muted-foreground">(system)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEditTag(tag)}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    {!tag.isSystem && (
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteTag(tag.tagId)}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Create / Edit form */}
          <div className="space-y-3 pt-2 border-t">
            <h4 className="text-sm font-medium">{editingTag ? 'Edit Tag' : 'New Tag'}</h4>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={tagForm.name}
                onChange={(e) => setTagForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Bug, Feature, VIP"
                maxLength={100}
              />
            </div>
            <div className="flex items-center gap-3">
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={tagForm.color}
                    onChange={(e) => setTagForm((f) => ({ ...f, color: e.target.value }))}
                    className="h-9 w-9 rounded border cursor-pointer"
                  />
                  <Input
                    value={tagForm.color}
                    onChange={(e) => setTagForm((f) => ({ ...f, color: e.target.value }))}
                    className="w-28"
                    maxLength={7}
                  />
                </div>
              </div>
              <div className="flex-1 space-y-2">
                <Label>Description (optional)</Label>
                <Input
                  value={tagForm.description}
                  onChange={(e) => setTagForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Optional description"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Preview:</span>
              {tagForm.name && <TagBadge name={tagForm.name} color={tagForm.color} />}
            </div>
          </div>

          <DialogFooter>
            {editingTag && (
              <Button
                variant="outline"
                onClick={() => {
                  setEditingTag(null);
                  setTagForm({ name: '', color: '#6B7280', description: '' });
                }}
              >
                Back to list
              </Button>
            )}
            <Button onClick={handleSaveTag} disabled={tagSaving || !tagForm.name.trim()}>
              {tagSaving ? 'Saving...' : editingTag ? 'Update Tag' : 'Create Tag'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
